import useTriggerPreview from '@/frontend/features/Fde/triggers/useTriggerPreview'
import useDashboardAnalyses from '@/frontend/features/Fde/triggers/useDashboardAnalyses'
import { useMemo, useRef, useState } from 'react'
import { randomUuid } from '@/frontend/lib/randomUuid'
import { Head, Link, router } from '@inertiajs/react'
import { ChevronLeft, Eye, Loader2, Play, Plus, Settings2, X, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import FdeShell from '@/frontend/features/Fde/FdeShell'
import FdeSelect from '@/frontend/features/Fde/FdeSelect'
import { Banner } from '@/frontend/features/Fde/triggers/components'
import { AskAgent, PreviewSummary } from '@/frontend/features/Fde/triggers/agentic'
import {
  AnalysisPicker, ColumnTags, FieldChips, FilterBuilder, Labeled, Node, NumberSetting, ObjectPicker, SourceChoices, TablePicker, Toggle,
} from '@/frontend/features/Fde/triggers/blocks'
import { CodeField } from '@/frontend/features/Fde/triggers/code'
import type { Draft, PickField } from '@/frontend/features/Fde/triggers/builder'
import {
  NEEDS_CONDITION, OPTIONAL_CONDITION, buildSpec, describeSource, draftFrom, emptyDraft, eventHelp, eventTitle, eventsFor,
  filterFields, guessIdentity, tableFields, thingWord, triggerSentence,
} from '@/frontend/features/Fde/triggers/builder'
import type { Preview, Trigger, TriggerSources, TriggerSpec, TriggersPageProps } from '@/frontend/features/Fde/triggers/types'
import { postJson, withProject } from '@/frontend/features/Fde/triggers/types'

interface Props extends TriggersPageProps {
  trigger: Trigger | null
}

const AGGS: Draft['measure']['agg'][] = ['count', 'sum', 'avg', 'min', 'max', 'count_distinct']
const COMPARE: Draft['threshold']['op'][] = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq']
const EMPTY_SOURCES: TriggerSources = { object_types: [], tables: [], analyses: [] }

function TriggerEditor(props: Props) {
  const copy = props.triggers_copy
  const projectId = props.project?.id
  const editing = props.trigger
  const initialSources = props.sources ?? EMPTY_SOURCES
  const [d, setD] = useState<Draft>(() => editing ? draftFrom(editing.spec, initialSources, props.praxis.available) : emptyDraft(props.praxis.available))
  const analyses = useDashboardAnalyses(initialSources, d.choice === 'analysis', withProject(`${props.paths.base}/analyses`, projectId))
  const sources = analyses.sources
  const [name, setName] = useState(editing?.name ?? '')
  const [description, setDescription] = useState(editing?.description ?? '')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(props.error ?? null)
  const [advanced, setAdvanced] = useState(false)
  const submission = useRef<{ fingerprint: string; key: string } | null>(null)
  const set = (patch: Partial<Draft>) => setD((current) => ({ ...current, ...patch }))

  const built = useMemo(() => buildSpec(d, sources, copy), [d, sources, copy])
  const live = useTriggerPreview(built.preview, withProject(`${props.paths.base}/preview`, projectId), editing?.id)
  const fields = filterFields(d, sources)
  const labels = useMemo(() => labelMap(d, sources), [d, sources])
  const suggestedName = built.preview ? `${describeSource(built.preview.source, copy)}${d.target === 'praxis' && d.workflow ? ` → ${d.workflow}` : ''}` : ''
  const thing = built.preview ? thingWord(built.preview.source, copy) : copy.thing_row

  const pickTable = (table: string) => {
    const tf = tableFields(sources, table)
    set({ table, originalSpec: undefined, identity: guessIdentity(tf), carry: [], filters: [], watch: [], groupBy: [], measure: { ...d.measure, field: '' } })
  }

  const save = async () => {
    if (!built.spec || saving) return
    const body = editing ? { spec: built.spec, note } : { name: name.trim() || suggestedName.slice(0, 160), description, spec: built.spec }
    const fingerprint = JSON.stringify([projectId, editing?.id, body])
    if (submission.current?.fingerprint !== fingerprint) submission.current = { fingerprint, key: randomUuid() }
    const requestKey = submission.current.key
    setSaving(true); setFailure(null)
    try {
      if (editing) {
        await postJson(withProject(`${props.paths.base}/${editing.id}/revisions`, projectId), body, undefined, requestKey)
        router.visit(withProject(`${props.paths.base}/${editing.id}`, projectId))
      } else {
        const out = await postJson<{ location: string }>(withProject(props.paths.base, projectId),
          body, undefined, requestKey)
        router.visit(out.location)
      }
    } catch (error) {
      setFailure((error as Error).message)
      setSaving(false)
    }
  }

  const back = editing ? withProject(`${props.paths.base}/${editing.id}`, projectId) : props.paths.index
  const columnsFromRun = live.preview?.columns ?? []
  const lintUrl = withProject(`${props.paths.base}/lint`, projectId)

  return (
    <>
      <Head title={editing ? copy.editor_edit : copy.editor_new} />
      <FdeShell copy={props.copy} eyebrow="" title={props.project?.name ?? props.organization.name} description="" projectId={projectId}
        >
        <div className="@container h-full overflow-y-auto bg-muted/10">
          <div className="mx-auto grid max-w-7xl gap-6 px-4 pb-28 pt-6 @3xl:px-6">
            <Link href={back} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ChevronLeft className="size-3.5" aria-hidden />{copy.back}</Link>
            <header className="flex flex-wrap items-end justify-between gap-4">
              <div className="grid min-w-0 flex-1 gap-2">
                <span className="text-xs font-semibold uppercase tracking-widest text-primary">{editing ? copy.editor_edit : copy.editor_new}</span>
                {editing ? <h2 className="truncate text-2xl font-semibold tracking-tight">{editing.name}</h2> : (
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={suggestedName || copy.name_placeholder}
                    className="h-11 max-w-3xl border-0 bg-transparent px-0 text-2xl font-semibold shadow-none focus-visible:ring-0" aria-label={copy.name} />
                )}
                {!editing && <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={copy.description_placeholder}
                  className="h-8 max-w-3xl border-0 bg-transparent px-0 text-sm text-muted-foreground shadow-none focus-visible:ring-0" aria-label={copy.description} />}
              </div>
              <AskAgent agents={props.trigger_agents} copy={copy} variant="outline" label={copy.ask_agent}
                title={editing ? copy.ask_fix_title : copy.ask_title}
                template={editing ? copy.ask_fix_template.split('{name}').join(editing.name).split('{id}').join(editing.id) : copy.ask_template} />
            </header>
            {failure && <Banner tone="error">{failure}</Banner>}

            <div className="grid items-start gap-6 @6xl:grid-cols-[minmax(0,1fr)_minmax(0,25rem)]">
              <div className="min-w-0">
                <Node index={1} icon={<Eye className="size-4" aria-hidden />} title={copy.step_watch} summary={copy.step_watch_help}>
                  <SourceChoices value={d.choice} copy={copy} onChange={(choice) => set({ choice, filters: [], watch: [], entity: [], event: eventsFor(choice)[0] })} />

                  {d.choice === 'objects' && (
                    <>
                      <ObjectPicker options={sources.object_types} value={d.objectTypeId} copy={copy}
                        onChange={(id) => set({ objectTypeId: id, filters: [], watch: [] })} />
                    </>
                  )}

                  {d.choice === 'table' && (
                    <>
                      <TablePicker tables={sources.tables} value={d.table} onChange={pickTable} copy={copy} error={sources.tables_error} />
                      {d.table && (
                        <>
                          <Labeled label={copy.identity_label} hint={copy.identity_hint}>
                            <FieldChips fields={fields} value={d.identity} onChange={(identity) => set({ identity })} copy={copy} limit={8} />
                          </Labeled>
                          <details className="group rounded-xl border border-border p-3">
                            <summary className="cursor-pointer text-sm font-medium">{copy.carry_label}</summary>
                            {copy.carry_hint && <p className="mb-2 mt-1 text-xs text-muted-foreground">{copy.carry_hint}</p>}
                            <FieldChips fields={fields} value={d.carry} onChange={(carry) => set({ carry })} copy={copy} limit={64} />
                          </details>
                        </>
                      )}
                    </>
                  )}

                  {d.choice === 'metric' && (
                    <MetricBlock d={d} set={set} sources={sources} copy={copy} fields={fields} pickTable={pickTable} />
                  )}

                  {d.choice === 'analysis' && (
                    <>
                      {analyses.busy && <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="size-3 animate-spin" aria-hidden />{copy.analyses_loading}</p>}
                      {!analyses.busy && <>
                      <AnalysisPicker analyses={sources.analyses ?? []} error={sources.analyses_error} copy={copy}
                        value={{ id: d.analysisId, computation: d.analysisComputation, table: d.analysisTable, filters: d.analysisFilters }}
                        onChange={(v) => set({ analysisId: v.id, analysisComputation: v.computation, analysisTable: v.table, analysisFilters: v.filters })} />
                      {d.analysisComputation && (
                        <Labeled label={copy.identity_label} hint={copy.identity_hint_code}>
                          <ColumnTags value={d.entity} onChange={(entity) => set({ entity })} suggestions={columnsFromRun} copy={copy} />
                        </Labeled>
                      )}
                      </>}
                    </>
                  )}

                  {d.choice === 'python' && (
                    <>
                      <Labeled label={copy.python_label} hint={copy.python_hint}>
                        <CodeField language="python" value={d.code} onChange={(code) => set({ code })} lintUrl={lintUrl} copy={copy} minLines={12} />
                      </Labeled>
                      <div className="grid gap-3 @2xl:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
                        <Labeled label={copy.output_label} hint={copy.output_hint}>
                          <Input value={d.output} onChange={(e) => set({ output: e.target.value })} className="font-mono text-xs" />
                        </Labeled>
                        <Labeled label={copy.identity_label} hint={copy.identity_hint_code}>
                          <ColumnTags value={d.entity} onChange={(entity) => set({ entity })} suggestions={columnsFromRun} copy={copy} />
                        </Labeled>
                      </div>
                    </>
                  )}

                  {d.choice === 'sql' && (
                    <>
                      <Labeled label={copy.sql} hint={copy.sql_hint}>
                        <CodeField language="sql" value={d.sql} onChange={(sql) => set({ sql })} lintUrl={lintUrl} copy={copy} minLines={6} />
                      </Labeled>
                      <Labeled label={copy.identity_label} hint={copy.identity_hint_code}>
                        <ColumnTags value={d.entity} onChange={(entity) => set({ entity })} suggestions={columnsFromRun} copy={copy} />
                      </Labeled>
                    </>
                  )}
                </Node>

                <Node index={2} icon={<Zap className="size-4" aria-hidden />} title={copy.step_when} summary={copy.step_when_help}>
                  <div className="grid gap-2 @2xl:grid-cols-2" role="radiogroup" aria-label={copy.step_when}>
                    {eventsFor(d.choice).map((event) => {
                      const on = d.event === event
                      return (
                        <button key={event} type="button" role="radio" aria-checked={on} onClick={() => set({ event })}
                          className={cn('grid gap-0.5 rounded-xl border p-3 text-left transition-colors',
                            on ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-foreground/20')}>
                          <span className={cn('text-sm font-medium', on && 'text-primary')}>{eventTitle(event, d.choice, copy)}</span>
                          {eventHelp(event, d.choice, copy) && <span className="text-xs text-muted-foreground">{eventHelp(event, d.choice, copy)}</span>}
                        </button>
                      )
                    })}
                  </div>
                  {d.event === 'changed' && (d.choice === 'objects' || d.choice === 'table') && (
                    <Labeled label={copy.watch_label} hint={copy.watch_hint}>
                      <FieldChips fields={fields.filter((f) => !currentIdentity(d, sources).includes(f.key))} value={d.watch}
                        onChange={(watch) => set({ watch })} copy={copy} />
                    </Labeled>
                  )}
                  {d.event === 'changed' && ['analysis', 'python', 'sql'].includes(d.choice) && (
                    <Labeled label={copy.watch_label} hint={copy.watch_hint_code}>
                      <ColumnTags value={d.watch} onChange={(watch) => set({ watch })} suggestions={columnsFromRun.filter((c) => !d.entity.includes(c))} copy={copy} />
                    </Labeled>
                  )}
                  {(d.choice === 'objects' || d.choice === 'table') && NEEDS_CONDITION.includes(d.event) && (
                    <Labeled label={copy.condition_label} hint={copy.condition_hint}>
                      <FilterBuilder fields={fields} value={d.filters} onChange={(filters) => set({ filters })} copy={copy} />
                    </Labeled>
                  )}
                  {(d.choice === 'objects' || d.choice === 'table') && OPTIONAL_CONDITION.includes(d.event) && (
                    <Labeled label={copy.only_records_label} hint={copy.only_records_hint}>
                      <FilterBuilder fields={fields} value={d.filters} onChange={(filters) => set({ filters })} copy={copy} />
                    </Labeled>
                  )}
                  <Toggle checked={d.fireNow} onChange={(fireNow) => set({ fireNow })} label={copy.fire_now_label}
                    hint={fill(d.fireNow ? copy.fire_now_on : copy.fire_now_off, { rows: live.preview?.rows ?? '…' })} />
                </Node>

                <Node index={3} icon={<Play className="size-4" aria-hidden />} title={copy.step_then} summary={copy.step_then_help} last>
                  <div className="grid gap-2 @2xl:grid-cols-2">
                    {(['praxis', 'none'] as const).map((kind) => (
                      <button key={kind} type="button" aria-pressed={d.target === kind} disabled={kind === 'praxis' && !props.praxis.available}
                        onClick={() => set({ target: kind })}
                        className={cn('grid gap-0.5 rounded-xl border p-3 text-left transition-colors disabled:opacity-50',
                          d.target === kind ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-foreground/20')}>
                        <span className={cn('text-sm font-medium', d.target === kind && 'text-primary')}>{copy[`target_${kind}`]}</span>
                        <span className="text-xs text-muted-foreground">{kind === 'praxis' && !props.praxis.available ? copy.praxis_missing : copy[`target_${kind}_help`]}</span>
                      </button>
                    ))}
                  </div>
                  {d.target === 'praxis' && (
                    <div className="grid gap-3">
                      <Labeled label={`${copy.praxis_workflow}${props.praxis.tenant ? ` · ${props.praxis.tenant}` : ''}`} hint={props.praxis.error}>
                        {props.praxis.names.length > 0 ? (
                          <FdeSelect value={d.workflow} placeholder={copy.choose_workflow} ariaLabel={copy.praxis_workflow} required
                            options={props.praxis.names.map((n) => ({ value: n, label: n }))} onValueChange={(workflow) => set({ workflow })} />
                        ) : (
                          <Input value={d.workflow} className="font-mono text-xs" onChange={(e) => set({ workflow: e.target.value })} />
                        )}
                      </Labeled>
                      <div className="grid gap-2 @2xl:grid-cols-2">
                        {[false, true].map((batch) => (
                          <button key={String(batch)} type="button" aria-pressed={d.batch === batch} onClick={() => set({ batch })}
                            className={cn('grid gap-0.5 rounded-xl border p-3 text-left text-sm transition-colors',
                              d.batch === batch ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/20')}>
                            <span className="font-medium">{fill(batch ? copy.batch_all : copy.batch_one, { thing })}</span>
                            {(batch ? copy.batch_all_help : copy.batch_one_help) && <span className="text-xs text-muted-foreground">{batch ? copy.batch_all_help : copy.batch_one_help}</span>}
                          </button>
                        ))}
                      </div>
                      <ExtraInputs value={d.extra} onChange={(extra) => set({ extra })} copy={copy} />
                      <details className="rounded-xl border border-border p-3 text-sm">
                        <summary className="cursor-pointer font-medium">{copy.input_shape}</summary>
                        <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">
                          {JSON.stringify(inputExample(d, live.preview, built.preview), null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </Node>

                <div className="mt-4 rounded-2xl border border-border bg-background">
                  <button type="button" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced}
                    className="flex w-full items-center justify-between gap-3 p-4 text-left">
                    <span className="flex items-center gap-2 text-sm font-semibold"><Settings2 className="size-4" aria-hidden />{copy.advanced}</span>
                    <span className="text-xs text-muted-foreground">{fill(copy.advanced_summary, { minutes: d.every, events: d.maxEvents })}</span>
                  </button>
                  {advanced && (
                    <div className="grid gap-3 border-t border-border p-4 @2xl:grid-cols-2">
                      <NumberSetting label={copy.every} hint={copy.every_hint} value={d.every} min={1} max={1440} suffix={copy.minutes}
                        onChange={(v) => set({ every: Math.max(1, Number(v) || 15) })} />
                      <NumberSetting label={copy.staleness} hint={copy.staleness_hint} value={d.staleness} min={1} suffix={copy.hours}
                        onChange={(v) => set({ staleness: Math.max(1, Number(v) || 36) })} />
                      <Toggle checked={d.requireSync} onChange={(requireSync) => set({ requireSync })} label={copy.require_sync} hint={copy.require_sync_hint} />
                      <Toggle checked={d.allowPartial} onChange={(allowPartial) => set({ allowPartial })} label={copy.allow_partial} hint={copy.allow_partial_hint} />
                      <NumberSetting label={copy.exit_share} hint={copy.exit_share_hint} value={Math.round(d.exitShare * 100)} min={5} max={100} suffix="%"
                        onChange={(v) => set({ exitShare: Math.min(1, Math.max(0.05, (Number(v) || 80) / 100)) })} />
                      <NumberSetting label={copy.max_events} hint={copy.max_events_hint} value={d.maxEvents} min={1}
                        onChange={(v) => set({ maxEvents: Math.max(1, Number(v) || 200) })} />
                      <NumberSetting label={copy.min_rows} hint={copy.min_rows_hint} value={d.minRows} min={0} onChange={(minRows) => set({ minRows })} />
                      {d.choice !== 'metric' && (
                        <NumberSetting label={copy.max_rows} hint={copy.max_rows_hint} value={d.maxRows} min={1} max={500000}
                          onChange={(v) => set({ maxRows: Math.max(1, Number(v) || 50000) })} />
                      )}
                    </div>
                  )}
                </div>
              </div>

              <aside className="grid content-start gap-3 @6xl:sticky @6xl:top-4">
                <div className="grid gap-3 rounded-2xl border border-border bg-background p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">{copy.live_title}</h3>
                    <Button size="sm" variant="ghost" onClick={live.refresh} disabled={!built.preview || live.busy}>
                      {live.busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Play className="size-4" aria-hidden />}
                      {built.preview?.source.kind === 'python' ? copy.run_analysis : copy.refresh}
                    </Button>
                  </div>
                  {built.preview && (
                    <p className="rounded-lg bg-primary/5 px-3 py-2 text-sm font-medium leading-relaxed text-foreground">{triggerSentence(built.preview, copy, labels)}</p>
                  )}
                  {built.missing && <p className="text-sm text-amber-700 dark:text-amber-300">{built.missing}</p>}
                  {built.preview?.source.kind === 'python' && !live.preview && !live.busy && (
                    <p className="text-sm text-muted-foreground">{copy.python_preview_hint}</p>
                  )}
                  {live.error && <Banner tone="error">{live.error}</Banner>}
                  {live.preview && built.preview && <PreviewSummary preview={live.preview} copy={copy} labels={labels} start={built.preview.start} />}
                </div>
              </aside>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 z-10 border-t border-border bg-background/95 px-4 py-3 backdrop-blur @3xl:px-6">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-end gap-2">
            {editing && <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={copy.note} className="h-9 max-w-md flex-1" />}
            <span className="mr-auto text-xs text-muted-foreground">{editing ? copy.editor_revision_help : copy.editor_help}</span>
            <Button variant="outline" asChild><Link href={back}>{copy.cancel}</Link></Button>
            <Button onClick={save} disabled={!built.spec || saving}>
              {saving && <Loader2 className="size-4 animate-spin" aria-hidden />}{saving ? copy.saving : editing ? copy.save_version : copy.save_draft}
            </Button>
          </div>
        </div>
      </FdeShell>
    </>
  )
}

function MetricBlock({ d, set, sources, copy, fields, pickTable }: {
  d: Draft; set: (p: Partial<Draft>) => void; sources: TriggerSources; copy: Record<string, string>; fields: PickField[]; pickTable: (t: string) => void
}) {
  const numeric = fields.filter((f) => !f.technical)
  return (
    <div className="grid gap-4">
      <div className="flex gap-2">
        {(['objects', 'table'] as const).map((on) => (
          <Button key={on} size="sm" variant={d.metricOn === on ? 'default' : 'outline'}
            onClick={() => set({ metricOn: on, objectTypeId: '', table: '', filters: [], groupBy: [], measure: { ...d.measure, field: '' } })}>
            {copy[`metric_on_${on}`]}
          </Button>
        ))}
      </div>
      {d.metricOn === 'objects'
        ? <ObjectPicker options={sources.object_types} value={d.objectTypeId} copy={copy} onChange={(id) => set({ objectTypeId: id, filters: [], groupBy: [], measure: { ...d.measure, field: '' } })} />
        : <TablePicker tables={sources.tables} value={d.table} onChange={pickTable} copy={copy} error={sources.tables_error} />}
      {fields.length > 0 && (
        <>
          <div className="grid gap-3 @2xl:grid-cols-2">
            <Labeled label={copy.measure_label}>
              <div className="grid grid-cols-2 gap-2">
                <FdeSelect value={d.measure.agg} ariaLabel={copy.measure_label} required
                  options={AGGS.map((agg) => ({ value: agg, label: copy[`agg_${agg}`] }))}
                  onValueChange={(agg) => set({ measure: { agg: agg as Draft['measure']['agg'], field: agg === 'count' ? '' : d.measure.field } })} />
                {d.measure.agg !== 'count' && (
                  <FdeSelect value={d.measure.field} placeholder={copy.choose_field} ariaLabel={copy.measure_field} required
                    options={numeric.map((f) => ({ value: f.field, label: f.label }))} onValueChange={(field) => set({ measure: { ...d.measure, field } })} />
                )}
              </div>
            </Labeled>
            <Labeled label={copy.threshold_label}>
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2">
                <FdeSelect value={d.threshold.op} ariaLabel={copy.threshold_label} required
                  options={COMPARE.map((op) => ({ value: op, label: copy[`op_${op}_short`] ?? copy[`op_${op}`] }))}
                  onValueChange={(op) => set({ threshold: { ...d.threshold, op: op as Draft['threshold']['op'] } })} />
                <Input type="number" value={d.threshold.value} onChange={(e) => set({ threshold: { ...d.threshold, value: e.target.value } })} />
              </div>
            </Labeled>
          </div>
          <Labeled label={copy.group_label} hint={copy.group_hint}>
            <FieldChips fields={fields.map((f) => ({ ...f, key: f.field }))} value={d.groupBy} onChange={(groupBy) => set({ groupBy })} copy={copy} limit={4} />
          </Labeled>
          <Labeled label={copy.only_records_label} hint={copy.metric_filter_hint}>
            <FilterBuilder fields={fields} value={d.filters} onChange={(filters) => set({ filters })} copy={copy} />
          </Labeled>
        </>
      )}
    </div>
  )
}

function ExtraInputs({ value, onChange, copy }: { value: Draft['extra']; onChange: (v: Draft['extra']) => void; copy: Record<string, string> }) {
  return (
    <Labeled label={copy.extra_label} hint={copy.extra_hint}>
      <div className="grid gap-2">
        {value.map((item, index) => (
          <div key={index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_auto] gap-2">
            <Input className="h-8 font-mono text-xs" placeholder={copy.extra_key} value={item.key}
              onChange={(e) => onChange(value.map((v, i) => (i === index ? { ...v, key: e.target.value.replace(/[^A-Za-z0-9_]/g, '_') } : v)))} />
            <Input className="h-8" placeholder={copy.extra_value} value={item.value}
              onChange={(e) => onChange(value.map((v, i) => (i === index ? { ...v, value: e.target.value } : v)))} />
            <Button size="icon" variant="ghost" className="size-8" aria-label={copy.remove} onClick={() => onChange(value.filter((_, i) => i !== index))}>
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        ))}
        <div><Button size="sm" variant="outline" onClick={() => onChange([...value, { key: '', value: '' }])}><Plus className="size-4" aria-hidden />{copy.extra_add}</Button></div>
      </div>
    </Labeled>
  )
}

const fill = (text: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((out, [key, value]) => out.split(`{${key}}`).join(String(value)), text ?? '')

function currentIdentity(d: Draft, sources: TriggerSources): string[] {
  if (d.choice === 'objects') return sources.object_types.find((o) => o.id === d.objectTypeId)?.source?.identity ?? []
  return d.identity
}

/** Column key to the name people read, for the live table. */
function labelMap(d: Draft, sources: TriggerSources): Record<string, string> {
  const fields = filterFields(d, sources)
  const out: Record<string, string> = Object.fromEntries(fields.map((f) => [f.key, f.label]))
  if (d.choice === 'metric') {
    fields.forEach((f) => { out[f.field] = f.label })
    out.valor = 'Valor'
    out.escopo = 'Escopo'
  }
  return out
}

/** What the workflow receives for one event, filled with the first row of the live preview when there is one. */
function inputExample(d: Draft, preview: Preview | null, spec?: TriggerSpec) {
  const row = preview?.sample?.[0] ?? {}
  const entity = spec?.entity ?? []
  return {
    trigger: { id: '…', name: '…', revision: 1, project_id: '…' },
    event: { id: '…', kind: spec?.fire_on[0] ?? 'entered', entity: entity.map((k) => row[k] ?? `<${k}>`), created_at: '…', idempotency_key: '…' },
    data: Object.keys(row).length ? row : { '…': '…' },
    static: Object.fromEntries(d.extra.filter((e) => e.key).map((e) => [e.key, e.value])),
  }
}

TriggerEditor.layout = (page: React.ReactElement) => <DashboardLayout variant="fullscreen">{page}</DashboardLayout>
export default TriggerEditor
